import { pxToEm } from 'util/pxToEm';
import React, { ReactElement, useState } from 'react';
import { Box, Typography, CircularProgress, Checkbox, Link } from '@mui/material';
import { OrderApproveIcon } from 'components/icons';
import { useOrganizationLeaderTerms } from 'utils/organization-leader-terms';
import { Button } from '../Button';

export interface LeaderInvitePendingStateProps {
  requestedRole: string;
  organizationName?: string;
  schoolTermsUrl?: string;
  schoolTermsDisplayName?: string;
  onAccept: (acceptedTerms: boolean) => Promise<void>;
  onDecline: () => Promise<void>;
}

export const LeaderInvitePendingState = ({
  requestedRole,
  organizationName,
  schoolTermsUrl,
  schoolTermsDisplayName,
  onAccept,
  onDecline,
}: LeaderInvitePendingStateProps): ReactElement => {
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const { termsUrl, termsDisplayText, checkboxText } = useOrganizationLeaderTerms({
    termsUrl: schoolTermsUrl,
    termsDisplayName: schoolTermsDisplayName,
  });

  const handleAccept = async () => {
    if (isAccepting || isDeclining || !acceptedTerms) return;
    setIsAccepting(true);
    try {
      await onAccept(acceptedTerms);
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (isAccepting || isDeclining) return;
    setIsDeclining(true);
    try {
      await onDecline();
    } finally {
      setIsDeclining(false);
    }
  };

  const isLoading = isAccepting || isDeclining;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {/* Status Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: pxToEm(14),
            fontWeight: 500,
            color: '#383838',
          }}
        >
          You&apos;ve been invited to join as a leader
        </Typography>
      </Box>

      {/* Invite Card */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 12,
          backgroundColor: '#FFFFFF',
          border: '1px solid #E0E0E0',
        }}
      >
        <Typography
          sx={{
            fontSize: pxToEm(16),
            fontWeight: 500,
            color: '#383838',
          }}
        >
          {requestedRole}
        </Typography>
        <Typography
          sx={{
            fontSize: pxToEm(12),
            fontWeight: 500,
            color: '#383838',
          }}
        >
          {organizationName ? `Invited to join ${organizationName}` : 'Invited by admin'}
        </Typography>

        {/* Terms Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            mt: 16,
            p: 12,
            border: '1px solid #E0E0E0',
            borderRadius: '4px',
            backgroundColor: '#FAFAFA',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <OrderApproveIcon fontSize="small" sx={{ color: '#383838' }} />
            <Link
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: pxToEm(12),
                fontWeight: 500,
                color: '#23305F',
                textDecoration: 'underline',
                '&:hover': {
                  opacity: 0.7,
                },
              }}
            >
              {termsDisplayText}
            </Link>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Checkbox
              checked={acceptedTerms}
              onChange={e => setAcceptedTerms(e.target.checked)}
              disabled={isLoading}
              sx={{
                p: 0,
                '&.Mui-checked': {
                  color: '#23305F',
                },
              }}
            />
            <Typography
              sx={{
                fontSize: pxToEm(12),
                fontWeight: 400,
                color: '#383838',
                lineHeight: '18px',
              }}
            >
              {checkboxText}
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            gap: 16,
            mt: 16,
          }}
        >
          <Typography
            onClick={handleDecline}
            sx={{
              fontSize: pxToEm(14),
              fontWeight: 400,
              color: '#23305F',
              cursor: isLoading ? 'default' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
              '&:hover': {
                opacity: isLoading ? 0.5 : 0.7,
              },
            }}
          >
            {isDeclining ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <CircularProgress size={12} sx={{ color: '#23305F' }} />
                Declining...
              </Box>
            ) : (
              'Decline'
            )}
          </Typography>
          <Button
            variant="default"
            onClick={handleAccept}
            disabled={isLoading || !acceptedTerms}
            sx={{
              fontSize: pxToEm(14),
              fontWeight: 400,
              height: 47,
              opacity: !acceptedTerms ? 0.5 : 1,
            }}
          >
            {isAccepting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <CircularProgress size={12} sx={{ color: '#fff' }} />
                Accepting...
              </Box>
            ) : (
              'Accept'
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
